/**
 * eSign — Envelope detail (read-only). Shown for sent/completed/voided documents
 * (drafts open the editor instead). Matches eSign's "agreement detail": recipient
 * progress, the baked PDF preview, download, audit trail, and owner actions.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Download, Send, Ban, CheckCircle, Clock, Eye, XCircle } from 'lucide-react';
import esignApi from '../../../api/esign';
import PdfCanvas from '../components/PdfCanvas';
import { DOC_CANVAS } from '../../../components/ui/documentCanvas';
import CapturedDetailsPanel from '../components/CapturedDetailsPanel';

const STATUS_PILL = {
  draft: 'bg-gray-100 text-gray-600',
  pending: 'bg-amber-100 text-amber-800',
  completed: 'bg-green-100 text-green-800',
  voided: 'bg-purple-100 text-purple-700',
  deleted: 'bg-gray-100 text-gray-400',
};

const SR_ICON = {
  signed: <CheckCircle className="w-4 h-4 text-green-600" />,
  viewed: <Eye className="w-4 h-4 text-amber-500" />,
  declined: <XCircle className="w-4 h-4 text-red-500" />,
  pending: <Clock className="w-4 h-4 text-gray-400" />,
};

// RTA-style launcher: signing order 1=agent, 2=vendor, 3+=co-vendor.
const PARTY_LABEL = { agent: 'Agent', vendor: 'Vendor', vendor2: 'Co-vendor' };

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '');

// At-a-glance per-owner progress: sent -> delivered -> opened -> signed, showing
// every milestone reached so you can see who is holding things up (DOMA
// 13201157349 S2). Tolerant of the trimmed launch-signer shape (no first_viewed_at
// /signed_at), which still carries status + sent/delivered/opened.
const signerProgress = (r) => {
  if (r.status === 'signed') return `Signed${r.signed_at ? ` · ${fmt(r.signed_at)}` : ''}`;
  if (r.status === 'declined') return 'Declined';
  const stages = [];
  if (r.email_sent_at) stages.push('Sent');
  if (r.delivered_at) stages.push('Delivered');
  if (r.email_opened_at) stages.push('Opened');
  if (r.first_viewed_at) stages.push('Viewed link');
  return stages.length ? stages.join(' · ') : (r.status || 'Pending');
};

export default function EnvelopeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [env, setEnv] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [launching, setLaunching] = useState('');
  const [pdfSrc, setPdfSrc] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  const [tab, setTab] = useState('document');

  const load = useCallback(async () => {
    try {
      const { data } = await esignApi.getEnvelope(id);
      setEnv(data);
      // A draft belongs in the editor — but only for its owner. A super admin
      // viewing another agent's work agency-wide stays here on the read-only
      // detail view (the editor is owner-scoped), so "All Agents Agreements" can
      // open any row, drafts included.
      if (data.status === 'draft' && data.is_owner) navigate(`/esign/envelope/${id}`, { replace: true });
    } catch { toast.error('Could not load document.'); }
    try {
      const { data } = await esignApi.auditLogs(id);
      setLogs(data || []);
    } catch { /* audit is best-effort */ }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Load the PDF *through the API* as a blob (same-origin, HTTPS) rather than letting
  // react-pdf fetch the raw S3 URL — which the browser blocks on the https prod page
  // (mixed-content / S3 CORS), surfacing as "Couldn't display the document."
  // Re-runs when status/file flips (e.g. void bakes a VOID watermark) so the baked copy reloads.
  const envStatus = env?.status;
  const envFileUrl = env?.signed_url || env?.original_url;
  useEffect(() => {
    if (!envFileUrl) return undefined;
    let cancelled = false;
    let objectUrl = null;
    setPdfError(false);
    (async () => {
      try {
        // Baked, pre-filled PDF — the field values are only an on-screen overlay
        // on the signing page, so the raw file is blank. This detail viewer has
        // no overlay of its own, so it must render the baked copy (CG/Simon: the
        // document showed blank here). Bakes current values + signatures; for a
        // completed doc it renders the finalised signed version.
        const { data } = await esignApi.previewBlob(id);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setPdfSrc(objectUrl);
      } catch {
        // Stream endpoint unavailable (e.g. backend not yet redeployed) — fall back to
        // the direct file URL so the viewer still works wherever the browser allows it.
        if (!cancelled) setPdfSrc(envFileUrl);
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id, envStatus, envFileUrl]);

  const download = async () => {
    try {
      // Baked, pre-filled PDF (not the blank original). Matches the My Agreements
      // download and heals a completed-but-still-baking doc.
      const res = await esignApi.previewBlob(id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${env?.title || 'document'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not download.'); }
  };
  const resend = async () => {
    setBusy(true);
    try { const { data } = await esignApi.resend(id); toast.success(`Resent to ${data.resent ?? 0} recipient(s).`); }
    catch (e) { toast.error(e?.response?.data?.error || 'Resend failed.'); }
    finally { setBusy(false); }
  };
  const doVoid = async () => {
    if (!window.confirm('Void this document? A VOID watermark is applied and it cannot be undone.')) return;
    setBusy(true);
    try { await esignApi.void(id); toast.success('Voided.'); load(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Void failed.'); }
    finally { setBusy(false); }
  };

  // RTA-style per-party launcher actions.
  const signInRoom = async (s) => {
    setLaunching(`room-${s.recipient_id}`);
    try {
      const { data } = await esignApi.deliver(id, { recipientId: s.recipient_id, inPerson: true });
      const token = data?.sign_token;
      if (token) {
        // Hand this device to the signer; `return` brings the agent back here to
        // launch the next party once they're done.
        const ret = encodeURIComponent(`/esign/document/${id}`);
        navigate(`/esign/sign/${token}?agent=1&return=${ret}`);
      } else {
        toast.error('Could not start in-room signing.');
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not start in-room signing.');
    } finally { setLaunching(''); }
  };
  const sendOffsite = async (s) => {
    setLaunching(`offsite-${s.recipient_id}`);
    try {
      await esignApi.deliver(id, { recipientId: s.recipient_id, inPerson: false });
      toast.success(`Sent to ${s.name || s.email} to sign.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not send.');
    } finally { setLaunching(''); }
  };

  if (!env) return <div className="p-8 text-gray-400">Loading…</div>;

  const isOwner = env.is_owner;
  const isPending = env.status === 'pending';
  const isVoided = env.status === 'voided';
  const hasUnsigned = (env.sign_requests || []).some((sr) => sr.status !== 'signed');
  // RTA-style per-party launcher: owner-only, for a pending templated authority.
  const signers = env.launch_signers || [];
  const canLaunch = isOwner && !!env.authority_key && isPending && signers.length > 0;
  const signedCount = signers.filter((s) => s.status === 'signed').length;
  const anyDelivered = signers.some((s) => s.email_sent_at || s.status === 'viewed' || s.status === 'declined');
  // "Generated" = launcher-ready but nothing delivered/actioned yet (matches RTA).
  const isGenerated = canLaunch && !anyDelivered && signers.some((s) => s.status === 'pending');
  const statusText = isGenerated ? 'Generated' : env.status;
  const statusPill = isGenerated ? 'bg-blue-100 text-blue-800' : (STATUS_PILL[env.status] || 'bg-gray-100');
  // The first still-to-sign party — highlighted as "Next" in the launcher.
  const nextPartyId = (signers.find((s) => s.status !== 'signed' && s.status !== 'declined') || {}).sign_request_id;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <button onClick={() => navigate('/esign/agreements')} className="flex items-center gap-1 text-sm text-gray-500 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to agreements
      </button>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-gray-900 truncate">{env.title}</h1>
          <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-xs font-medium capitalize ${statusPill}`}>
            {statusText}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={download} className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border">
            <Download className="w-4 h-4" /> Download
          </button>
          {isOwner && isPending && hasUnsigned && (
            <button onClick={resend} disabled={busy} className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border disabled:opacity-50">
              <Send className="w-4 h-4" /> Resend
            </button>
          )}
          {isOwner && !isVoided && (
            <button onClick={doVoid} disabled={busy} className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border text-red-600 disabled:opacity-50">
              <Ban className="w-4 h-4" /> Void
            </button>
          )}
        </div>
      </div>

      {/* Captured details live on their own tab, connected to this e-sign record
          (authority records only — that's where the capture-only fields exist). */}
      {env.authority_key && (
        <div className="flex gap-6 border-b border-gray-200 mb-6">
          {[['document', 'Document'], ['captured', 'Captured details']].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`pb-3 -mb-px text-sm font-medium border-b-2 transition-colors ${
                tab === k ? 'border-teal-600 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {tab === 'captured' && env.authority_key ? (
        <CapturedDetailsPanel envelopeId={id} canEdit={isOwner} />
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        {/* PDF preview */}
        <div className={`${DOC_CANVAS} rounded-xl p-4 order-2 lg:order-1`}>
          <PdfCanvas fileUrl={pdfSrc} loading={!pdfSrc && !pdfError} width={720} />
        </div>

        {/* Recipients + audit */}
        <div className="order-1 lg:order-2 space-y-6">
          {/* RTA-style signing launcher — each party can sign in the room or be sent offsite */}
          {canLaunch && (
            <div className="bg-white border rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase">Signing</h3>
                <span className="text-xs text-gray-400">{signedCount} of {signers.length} signed</span>
              </div>
              <div className="space-y-4">
                {signers.map((s) => {
                  const done = s.status === 'signed';
                  const declined = s.status === 'declined';
                  const isNext = s.sign_request_id === nextPartyId;
                  return (
                    <div key={s.sign_request_id} className="border-b last:border-0 pb-3 last:pb-0">
                      <div className="flex items-start gap-2">
                        <div className="mt-0.5">{SR_ICON[s.status] || SR_ICON.pending}</div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {PARTY_LABEL[s.party] || 'Signer'}: {s.name || s.email}
                            {isNext && (
                              <span className="ml-2 align-middle px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 text-[10px] font-semibold uppercase">Next</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 truncate">{s.email}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {signerProgress(s)}
                          </div>
                        </div>
                      </div>
                      {!done && !declined && (
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => signInRoom(s)} disabled={!!launching}
                            className="flex-1 text-xs px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                            {launching === `room-${s.recipient_id}` ? '…' : 'Sign in room'}
                          </button>
                          <button onClick={() => sendOffsite(s)} disabled={!!launching}
                            className="flex-1 text-xs px-3 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50">
                            {launching === `offsite-${s.recipient_id}` ? '…' : 'Send offsite'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!canLaunch && (
          <div className="bg-white border rounded-xl p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Recipients</h3>
            <div className="space-y-3">
              {(env.sign_requests || []).map((sr) => (
                <div key={sr.id} className="flex items-start gap-2">
                  <div className="mt-0.5">{SR_ICON[sr.status] || SR_ICON.pending}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">{sr.signer_name || sr.signer_email}</div>
                    <div className="text-xs text-gray-400 truncate">{sr.signer_email}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {signerProgress(sr)}
                    </div>
                  </div>
                </div>
              ))}
              {(!env.sign_requests || !env.sign_requests.length) && (
                <p className="text-sm text-gray-400">No signers.</p>
              )}
            </div>
          </div>
          )}

          <div className="bg-white border rounded-xl p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Activity</h3>
            <div className="space-y-2">
              {logs.length === 0 && <p className="text-sm text-gray-400">No activity yet.</p>}
              {logs.map((l) => (
                <div key={l.id} className="text-xs text-gray-600">
                  <span className="capitalize font-medium">{(l.event_type || '').replace(/_/g, ' ')}</span>
                  {l.actor_id_or_email ? ` — ${l.actor_id_or_email}` : ''}
                  <div className="text-gray-400">{fmt(l.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
